import { PipelineStepType } from './pipelineBuilder.types'
import { TransitionConfig } from './transitionConfig.types'
import {
    LEADS_EXIT_TRIGGER_OPTIONS,
    labelForTrigger,
    PM_MANUAL_TRIGGER,
} from './transitionTriggerCatalog'

export const DEFAULT_LEADS_EXIT_TRIGGERS = LEADS_EXIT_TRIGGER_OPTIONS.map(
    (option) => option.value,
)

export function defaultLeadsExitTransitionConfig(): TransitionConfig {
    return {
        exit_mode: 'all',
        triggers: [...DEFAULT_LEADS_EXIT_TRIGGERS],
        allow_pm_override: true,
        require_pm_approval: false,
    }
}

export function normalizeLeadsExitTriggers(
    rawTriggers: string[] | undefined,
): string[] {
    const allowed = new Set(DEFAULT_LEADS_EXIT_TRIGGERS)
    if (!rawTriggers?.length) {
        return [...DEFAULT_LEADS_EXIT_TRIGGERS]
    }
    const filtered = rawTriggers.filter(
        (trigger) => allowed.has(trigger) && trigger !== PM_MANUAL_TRIGGER,
    )
    return filtered.length > 0 ? filtered : [...DEFAULT_LEADS_EXIT_TRIGGERS]
}

export function normalizeLeadsExitConfig(
    config: Partial<TransitionConfig> | undefined,
): TransitionConfig {
    const legacyTriggers = config?.triggers
    const hadPmManual = legacyTriggers?.includes(PM_MANUAL_TRIGGER) ?? false
    const triggers = normalizeLeadsExitTriggers(legacyTriggers)

    let allowPmOverride = config?.allow_pm_override
    if (allowPmOverride === undefined) {
        allowPmOverride = hadPmManual || true
    }

    return {
        exit_mode: 'all',
        triggers,
        allow_pm_override: Boolean(allowPmOverride),
        require_pm_approval: false,
    }
}

export function transitionConfigFromAllMode(
    triggers: string[],
    allowPmOverride: boolean,
): TransitionConfig {
    return {
        exit_mode: 'all',
        triggers: normalizeLeadsExitTriggers(triggers),
        allow_pm_override: allowPmOverride,
        require_pm_approval: false,
    }
}

export function labelForLeadsExitTrigger(trigger: string): string {
    return labelForTrigger('leads', trigger)
}

export function summarizeAllModeExit(
    triggers: string[],
    allowPmOverride: boolean,
    fromType: PipelineStepType = 'leads',
): string {
    const conditionTriggers = triggers.filter(
        (trigger) => trigger !== PM_MANUAL_TRIGGER,
    )
    if (conditionTriggers.length === 0) {
        return 'No exit conditions configured'
    }

    const labels = conditionTriggers.map((trigger) =>
        labelForTrigger(fromType, trigger),
    )
    const conditions =
        labels.length === 1
            ? labels[0]
            : labels.slice(0, -1).join(', ') +
              ' and ' +
              labels[labels.length - 1]

    const parts = [`All complete: ${conditions}`]
    if (allowPmOverride) {
        parts.push('PM override allowed')
    }
    return parts.join(' · ')
}
